import { boot } from 'quasar/wrappers'
import { isLocalhost } from 'src/config'
import { ERROR_TYPES } from 'src/consts/analytics'
import { log } from 'src/services/log.service'

const CHUNK_ERROR_PATTERNS = [
  'Failed to fetch dynamically imported module',
  'ChunkLoadError',
  'Loading chunk',
  'Loading CSS chunk',
]

let reloadTriggered = false
let handlersInitialized = false

export default boot(() => {
  if (isLocalhost) return log.info('[Version Control] Disabled in development mode')

  if (!handlersInitialized) {
    handlersInitialized = true
    setupFetchInterceptor()
    setupChunkErrorHandler()
  }
})

function triggerReload() {
  if (reloadTriggered) return
  reloadTriggered = true
  window.location.reload()
}

function setupChunkErrorHandler() {
  window.addEventListener(
    'error',
    (event) => {
      if (
        event.target &&
        (event.target.tagName === 'SCRIPT' || event.target.tagName === 'LINK') &&
        (event.target.src?.includes('/assets/') || event.target.href?.includes('/assets/'))
      ) {
        triggerReload()
        event.preventDefault()
      }
    },
    true
  )
}

function setupFetchInterceptor() {
  const originalFetch = window.fetch.bind(window)

  window.fetch = async (...args) => {
    const url = args[0]?.url ?? args[0]
    const isAssetRequest = typeof url === 'string' && url.includes('/assets/')

    try {
      const response = await originalFetch(...args)
      if (isAssetRequest && response.status === 404) {
        triggerReload()
      }
      return response
    } catch (error) {
      if (isAssetRequest) {
        triggerReload()
        error.name = ERROR_TYPES.VERSION_CONTROL
      }
      throw error
    }
  }
}

/**
 * Call from router.onError. If the error is a chunk/asset load error, triggers reload
 * (once). Caller should rethrow so the router still treats the navigation as failed.
 */
export function handleNavigationError(error) {
  if (isChunkOrAssetLoadError(error)) {
    triggerReload()
  }
}

/**
 * Returns true if the error message or error.cause indicates a chunk/asset load failure
 * (e.g. after a new deployment with stale chunks).
 */
function isChunkOrAssetLoadError(error) {
  if (error == null) return false
  const strings = [
    String(error),
    error?.message,
    error?.cause != null ? String(error.cause) : '',
  ].filter(Boolean)
  return strings.some((str) => CHUNK_ERROR_PATTERNS.some((pattern) => str.includes(pattern)))
}
